/**
 * Master Data API client methods.
 *
 * Handles fetching master data for sectors, industries, and currencies.
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../../utils/errorHandler';

export interface SectorMaster {
  id: number;
  name: string;
  /** The stable vocabulary code (`information-technology`). This — not `name` —
   *  is the option id and the value a position stores, so a rename or a locale
   *  never changes what a row holds (D18). */
  code: string;
  description?: string;
}

export interface IndustryMaster {
  id: number;
  name: string;
  description?: string;
  sector_id?: number;
}

/**
 * Get all sectors for autocomplete dropdown.
 */
export async function getSectors(): Promise<SectorMaster[]> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/financial-data/master/sectors`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get sectors' }));
      const error = new Error(errorData.detail || 'Failed to get sectors');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get all industries for autocomplete dropdown.
 *
 * @param sectorId - Optional sector ID to filter industries
 */
export async function getIndustries(sectorId?: number): Promise<IndustryMaster[]> {
  const startTime = Date.now();
  const method = 'GET';
  let url = new URL(`${API_CONFIG.baseURL}/api/v1/financial-data/master/industries`);
  if (sectorId !== undefined) {
    url.searchParams.append('sector_id', sectorId.toString());
  }
  const urlStr = url.toString();

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, urlStr, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, urlStr, { sectorId });

    const response = await fetch(urlStr, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get industries' }));
      const error = new Error(errorData.detail || 'Failed to get industries');
      logApiError(method, urlStr, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, urlStr, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'master/industries', error, duration);
    throw error;
  }
}

/**
 * Get available currencies from fx_rates + USD.
 */
export async function getCurrencies(): Promise<string[]> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/financial-data/master/currencies`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get currencies' }));
      const error = new Error(errorData.detail || 'Failed to get currencies');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'master/currencies', error, duration);
    throw error;
  }
}
